import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { Camera, ChevronLeft, ChevronRight } from 'lucide-react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import type { VisualInteractionMethod } from '../../../api/http/collectionService'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'

type Block = Extract<WireArticleBlock, { type: 'photo-group' }>

// ─── Width → wrapper class (same scheme as PhotoBlock) ────────────────────────
const WIDTH_CLASS: Record<NonNullable<Block['width']>, string> = {
  column: 'mx-auto max-w-3xl',
  wide:   'mx-auto max-w-4xl',
  full:   'w-full',
  // Break out of the article container — true wall-to-wall bleed
  bleed:  'w-screen ml-[calc(50%-50vw)]',
}

// ─── Shared caption row ────────────────────────────────────────────────────────
function PhotoCaption({ classification, disseminationControls, caption, captionClassification, captionDisseminationControls, credit }: {
  classification: string
  disseminationControls?: string[]
  caption?: string
  captionClassification?: string
  captionDisseminationControls?: string[]
  credit?: string
}) {
  return (
    <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-secondary">
      <Camera size={10} aria-hidden="true" />
      <PortionMark classification={classification} disseminationControls={disseminationControls} />
      {credit && <><span aria-hidden="true">·</span><span className="italic opacity-70">{credit}</span></>}
      {caption && <><span aria-hidden="true">·</span><PortionMark classification={captionClassification} disseminationControls={captionDisseminationControls} />{caption}</>}
    </figcaption>
  )
}

// ─── Grid layout ──────────────────────────────────────────────────────────────
function GridLayout({ block, emitInteraction }: { block: Block; emitInteraction: (method: VisualInteractionMethod) => void }) {
  const artifacts = useArticleArtifacts()
  const { photos, display, aspectRatio = 'natural' } = block
  const columns = display.type === 'grid' ? (display.columns ?? 2) : 2
  const aspectStyle: React.CSSProperties = aspectRatio !== 'natural'
    ? { aspectRatio: aspectRatio.replace(':', ' / ') }
    : {}

  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {photos.map((p) => (
        <figure key={p.id} className="my-0">
          <img
            src={artifacts.find(a => a.id === p.artifactId)?.presignedUrl ?? ''}
            alt={p.altText ?? ''}
            className="w-full rounded object-cover"
            onPointerEnter={() => emitInteraction('HOVER_OVER')}
            style={{
              ...aspectStyle,
              ...(p.focalPoint ? { objectPosition: `${p.focalPoint.x * 100}% ${p.focalPoint.y * 100}%` } : {}),
            }}
          />
          <PhotoCaption classification={p.classification} disseminationControls={p.disseminationControls} caption={p.caption} captionClassification={p.captionClassification} captionDisseminationControls={p.captionDisseminationControls} credit={p.credit} />
        </figure>
      ))}
    </div>
  )
}

// ─── Default carousel ─────────────────────────────────────────────────────────
function DefaultCarousel({ block, emitInteraction }: { block: Block; emitInteraction: (method: VisualInteractionMethod) => void }) {
  const artifacts = useArticleArtifacts()
  const { photos, aspectRatio = 'natural' } = block
  const parallax = block.display.type === 'carousel' && block.display.parallax

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const tweenFactor = 0.5
  const tweenNodes  = useRef<HTMLElement[]>([])

  const setTweenNodes = useCallback(() => {
    if (!emblaApi) return
    tweenNodes.current = emblaApi.slideNodes().map(
      (node) => node.querySelector('[data-parallax-layer]') as HTMLElement
    ).filter(Boolean)
  }, [emblaApi])

  const applyParallax = useCallback(() => {
    if (!emblaApi || !parallax) return
    const scrollProgress = emblaApi.scrollProgress()
    const snapList       = emblaApi.scrollSnapList()
    tweenNodes.current.forEach((node, i) => {
      if (!node) return
      const snapOffset  = snapList[i] ?? 0
      const diffToTarget = snapOffset - scrollProgress
      node.style.transform = `translateX(${diffToTarget * tweenFactor * -100}%)`
    })
  }, [emblaApi, parallax])

  useEffect(() => {
    if (!emblaApi) return
    setTweenNodes()
    applyParallax()
    emblaApi.on('reInit',  setTweenNodes)
    emblaApi.on('reInit',  applyParallax)
    emblaApi.on('scroll',  applyParallax)
    emblaApi.on('select',  () => setSelectedIndex(emblaApi.selectedScrollSnap()))
    return () => emblaApi.destroy()
  }, [emblaApi, setTweenNodes, applyParallax])

  const aspectStyle: React.CSSProperties = aspectRatio !== 'natural'
    ? { aspectRatio: aspectRatio.replace(':', ' / ') }
    : {}

  const selected = photos[selectedIndex]

  return (
    <div className="relative">
      <div className="overflow-hidden rounded" ref={emblaRef} onPointerDown={() => emitInteraction('DRAG')}>
        <div className="flex">
          {photos.map((p) => (
            <div key={p.id} className="min-w-0 flex-[0_0_100%]" style={{ overflow: parallax ? 'hidden' : undefined }}>
              <img
                data-parallax-layer
                src={artifacts.find(a => a.id === p.artifactId)?.presignedUrl ?? ''}
                alt={p.altText ?? ''}
                className="w-full object-cover"
                style={{
                  ...aspectStyle,
                  ...(p.focalPoint ? { objectPosition: `${p.focalPoint.x * 100}% ${p.focalPoint.y * 100}%` } : {}),
                  ...(parallax ? { willChange: 'transform', width: '120%', marginLeft: '-10%' } : {}),
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Prev / Next */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => {
              emitInteraction('CLICK')
              emblaApi?.scrollPrev()
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
            aria-label="Previous"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => {
              emitInteraction('CLICK')
              emblaApi?.scrollNext()
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
            aria-label="Next"
          >
            <ChevronRight size={18} />
          </button>

          {/* Dot indicators */}
          <div className="mt-2 flex justify-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  emitInteraction('CLICK')
                  emblaApi?.scrollTo(i)
                }}
                className={`h-1.5 rounded-full transition-all ${
                  i === selectedIndex ? 'w-4 bg-fg' : 'w-1.5 bg-fg-secondary opacity-40'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Caption for selected slide */}
      {selected && (
        <PhotoCaption
          classification={selected.classification}
          disseminationControls={selected.disseminationControls}
          caption={selected.caption}
          captionClassification={selected.captionClassification}
          captionDisseminationControls={selected.captionDisseminationControls}
          credit={selected.credit}
        />
      )}
    </div>
  )
}

// ─── Thumbnail carousel ───────────────────────────────────────────────────────
function ThumbnailCarousel({ block, emitInteraction }: { block: Block; emitInteraction: (method: VisualInteractionMethod) => void }) {
  const artifacts = useArticleArtifacts()
  const { photos, aspectRatio = 'natural' } = block
  const parallax = block.display.type === 'carousel' && block.display.parallax

  const [mainRef, mainApi]   = useEmblaCarousel({ loop: true })
  const [thumbRef, thumbApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const tweenFactor = 0.5
  const tweenNodes  = useRef<HTMLElement[]>([])

  const setTweenNodes = useCallback(() => {
    if (!mainApi) return
    tweenNodes.current = mainApi.slideNodes().map(
      (node) => node.querySelector('[data-parallax-layer]') as HTMLElement
    ).filter(Boolean)
  }, [mainApi])

  const applyParallax = useCallback(() => {
    if (!mainApi || !parallax) return
    const scrollProgress = mainApi.scrollProgress()
    const snapList       = mainApi.scrollSnapList()
    tweenNodes.current.forEach((node, i) => {
      if (!node) return
      const snapOffset   = snapList[i] ?? 0
      const diffToTarget = snapOffset - scrollProgress
      node.style.transform = `translateX(${diffToTarget * tweenFactor * -100}%)`
    })
  }, [mainApi, parallax])

  // Sync: main → thumb
  const onMainSelect = useCallback(() => {
    if (!mainApi || !thumbApi) return
    const idx = mainApi.selectedScrollSnap()
    setSelectedIndex(idx)
    thumbApi.scrollTo(idx)
  }, [mainApi, thumbApi])

  useEffect(() => {
    if (!mainApi) return
    setTweenNodes()
    applyParallax()
    mainApi.on('reInit', setTweenNodes)
    mainApi.on('reInit', applyParallax)
    mainApi.on('scroll', applyParallax)
    mainApi.on('select', onMainSelect)
    return () => mainApi.destroy()
  }, [mainApi, setTweenNodes, applyParallax, onMainSelect])

  const aspectStyle: React.CSSProperties = aspectRatio !== 'natural'
    ? { aspectRatio: aspectRatio.replace(':', ' / ') }
    : {}

  const selected = photos[selectedIndex]

  return (
    <div>
      {/* Main slide */}
      <div className="relative mb-2">
        <div className="overflow-hidden rounded" ref={mainRef} onPointerDown={() => emitInteraction('DRAG')}>
          <div className="flex">
            {photos.map((p) => (
              <div key={p.id} className="min-w-0 flex-[0_0_100%]" style={{ overflow: parallax ? 'hidden' : undefined }}>
                <img
                  data-parallax-layer
                  src={artifacts.find(a => a.id === p.artifactId)?.presignedUrl ?? ''}
                  alt={p.altText ?? ''}
                  className="w-full object-cover"
                  style={{
                    ...aspectStyle,
                    ...(p.focalPoint ? { objectPosition: `${p.focalPoint.x * 100}% ${p.focalPoint.y * 100}%` } : {}),
                    ...(parallax ? { willChange: 'transform', width: '120%', marginLeft: '-10%' } : {}),
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => {
                emitInteraction('CLICK')
                mainApi?.scrollPrev()
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => {
                emitInteraction('CLICK')
                mainApi?.scrollNext()
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* Caption */}
      {selected && (
        <PhotoCaption
          classification={selected.classification}
          disseminationControls={selected.disseminationControls}
          caption={selected.caption}
          captionClassification={selected.captionClassification}
          captionDisseminationControls={selected.captionDisseminationControls}
          credit={selected.credit}
        />
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="overflow-hidden mt-2" ref={thumbRef}>
          <div className="flex gap-1.5">
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  emitInteraction('CLICK')
                  mainApi?.scrollTo(i)
                }}
                className={`relative shrink-0 overflow-hidden rounded transition-opacity ${
                  i === selectedIndex
                    ? 'ring-2 ring-accent-primary-solid opacity-100'
                    : 'opacity-50 hover:opacity-80'
                }`}
                style={{ width: 64, height: 48 }}
                aria-label={`Go to photo ${i + 1}`}
              >
                <img
                  src={artifacts.find(a => a.id === p.artifactId)?.presignedUrl ?? ''}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── PhotoGroupBlock ──────────────────────────────────────────────────────────
export function PhotoGroupBlock({ block }: { block: Block }) {
  const engagement = useVisualEngagementContext()
  const figureRef = useRef<HTMLElement>(null)
  const { photos, width = 'column', display, groupTitle, groupTitleClassification, groupTitleDisseminationControls } = block
  const wrapperClass = WIDTH_CLASS[width]
  const visualId = block.id

  useVisualInViewTracking(engagement, figureRef, visualId, 'GALLERY')

  const emitInteraction = (method: VisualInteractionMethod) => {
    emitVisualInteraction(engagement, visualId, 'GALLERY', method)
  }

  const isCarousel  = display.type === 'carousel'
  const isThumbnail = isCarousel && display.style === 'thumbnails'

  return (
    <figure ref={figureRef} className={`my-0 ${wrapperClass}`}>
      {groupTitle && (
        <p className="mb-2 text-sm font-semibold text-fg">
          <PortionMark classification={groupTitleClassification} disseminationControls={groupTitleDisseminationControls} />
          {groupTitle}
        </p>
      )}

      {!isCarousel && <GridLayout block={block} emitInteraction={emitInteraction} />}
      {isCarousel && !isThumbnail && <DefaultCarousel block={block} emitInteraction={emitInteraction} />}
      {isThumbnail && <ThumbnailCarousel block={block} emitInteraction={emitInteraction} />}

      {/* Group-wide credit if no per-photo credits and all share the same */}
      {photos.length === 0 && (
        <figcaption className="mt-1.5 text-[11px] italic text-fg-secondary opacity-70">
          No photos
        </figcaption>
      )}
    </figure>
  )
}

